import { FC, useMemo, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Button,
  FormControl,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid-premium';
import {
  AccountGroupCategory,
  useGetAccountGroupDefaultsQuery,
  useUpdateAccountGroupDefaultMutation,
} from '@/store/accountsApi';
import { GridContainer } from '../../components/grids/GridContainer';

interface SetAccountGroupDefaultsProps {
  accountGroup: AccountGroupCategory;
}

type ClassificationRecord = {
  id: GridRowId;
  path: string[];
  name: string;
  tagId: number | null;
  classificationId: number;
};

export const SetAccountGroupDefaults: FC<SetAccountGroupDefaultsProps> = ({
  accountGroup,
}) => {
  const {
    data: defaultsData,
    isLoading,
    isError,
  } = useGetAccountGroupDefaultsQuery(accountGroup.TagCategoryId, {
    refetchOnMountOrArgChange: true,
  });
  const [updateDefault, { isLoading: isUpdating }] =
    useUpdateAccountGroupDefaultMutation();
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleUpdate = (classificationId: number, tagId: number | null) => {
    setEditingId(classificationId);
    updateDefault({ classificationId, tagId }).finally(() => {
      setEditingId(null);
    });
  };

  const { rows } = useMemo(() => {
    if (!defaultsData || !defaultsData.Records) {
      return { rows: [] };
    }

    const allRows: ClassificationRecord[] = [];

    const flatten = (nodes: any[], parentPath: string[] = []) => {
      for (const node of nodes) {
        const currentPath = [...parentPath, node.ClassificationId.toString()];
        const displayName =
          node.ClassificationName ||
          node.TagName ||
          node.Name ||
          `Classification ${node.ClassificationId}`;

        // Create the record
        const record: ClassificationRecord = {
          id: node.ClassificationId,
          path: currentPath,
          name: displayName,
          tagId: node.InvestorTagId || null,
          classificationId: node.ClassificationId,
        };

        allRows.push(record);

        // Recursively process children
        if (node.Children && node.Children.length > 0) {
          flatten(node.Children, currentPath);
        }
      }
    };

    flatten(defaultsData.Records);
    return { rows: allRows };
  }, [defaultsData]);

  const getTreeDataPath = useCallback((row: ClassificationRecord) => {
    return row.path;
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Balance Sheet Classification',
      flex: 1,
    },
    {
      field: 'tagId',
      headerName: 'Default Option',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params;
        if (isUpdating && editingId === row.classificationId) {
          return <CircularProgress size={24} />;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl
              size='small'
              sx={{ minWidth: 200, mr: 1 }}
            >
              <Select
                value={row.tagId ?? ''}
                onChange={(e) =>
                  handleUpdate(row.classificationId, e.target.value as number)
                }
                displayEmpty
                fullWidth
              >
                <MenuItem
                  value=''
                  disabled
                >
                  Select a default
                </MenuItem>
                {accountGroup.Tags.map((tag) => (
                  <MenuItem
                    key={tag.InvestorTagId}
                    value={tag.InvestorTagId}
                  >
                    {tag.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {row.tagId && (
              <Button
                size='small'
                onClick={() => handleUpdate(row.classificationId, null)}
              >
                Remove
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity='error'>
        Could not load default values. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert
        severity='info'
        sx={{ mb: 3 }}
      >
        Save time by setting the default value by account type. Every time an
        account is added, it will be given the default value for it's
        classification. The default can always be override by picking an option
        for an individual account.
      </Alert>

      <GridContainer
        isWidget={false}
        size='flyout'
      >
        <DataGridPremium
          treeData
          disablePivoting
          rows={rows}
          columns={columns}
          getTreeDataPath={getTreeDataPath}
          disableColumnMenu
          hideFooter
        />
      </GridContainer>
    </Box>
  );
};

export default SetAccountGroupDefaults;
